import { useEffect, useRef, useState } from "react";

/**
 * Official company logo. Variants: "color" | "mono" (navy) | "white".
 *
 * `swap` renders the mono logo at rest and reveals `swapTo` ("color" | "white") on hover/focus of the
 * nearest .group / .index-row / .logo-cell. The hover variant is only mounted once the logo is near the
 * viewport (or is hovered/focused), so a strip of logos never downloads two files per logo up front.
 *
 * Small renderings (<= 48px high) use the 96px "-s" WebP. Width is derived from the known aspect ratio,
 * so the layout never shifts while loading.
 */
const small = (src, height) => (height <= 48 && src ? src.replace(/\.webp$/, "-s.webp") : src);

export const CompanyLogo = ({ company, variant = "color", swap = false, swapTo = "color", height = 40, className = "", eager = false, ...rest }) => {
  const ref = useRef(null);
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!swap) return;
    const el = ref.current;
    if (!el) return;
    const host = el.closest(".group, .index-row, .logo-cell") || el;
    const arm = () => setArmed(true);
    host.addEventListener("pointerenter", arm, { once: true });
    host.addEventListener("focusin", arm, { once: true });
    let io;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { arm(); io.disconnect(); } }, { rootMargin: "50% 0px" });
      io.observe(el);
    } else arm();
    return () => {
      host.removeEventListener("pointerenter", arm);
      host.removeEventListener("focusin", arm);
      io?.disconnect();
    };
  }, [swap]);

  if (!company) return null;
  const pick = { color: company.logo, mono: company.logo_mono || company.logo, white: company.logo_white || company.logo };
  const base = small(pick[swap ? "mono" : variant], height);
  if (!base) return <span className={`text-[15px] font-semibold uppercase tracking-[0.12em] ${className}`}>{company.wordmark || company.name}</span>;
  const ratio = company.logo_ratio || 3;
  const w = Math.round(height * ratio);
  const common = { height, width: w, decoding: "async", draggable: false };

  if (!swap || !company.logo) {
    return <img src={base} alt={company.name} {...common} loading={eager ? "eager" : "lazy"} className={`block object-contain object-left ${className}`} style={{ height, width: w, maxWidth: "100%" }} {...rest} />;
  }
  const hover = small(pick[swapTo] || company.logo, height);
  return (
    <span ref={ref} className={`logo-swap relative inline-block ${className}`} style={{ height, width: w, maxWidth: "100%" }} {...rest}>
      <img src={base} alt={company.name} {...common} loading={eager ? "eager" : "lazy"} className="logo-swap-base absolute inset-0 h-full w-full object-contain object-left" />
      {armed && hover !== base && (
        <img src={hover} alt="" aria-hidden="true" {...common} loading="lazy" className="logo-swap-color absolute inset-0 h-full w-full object-contain object-left" />
      )}
    </span>
  );
};
